import { FilterChangeEvent, initValues } from './Filter';
import { Button, Form, Select, Switch } from 'antd';
import { ClearOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { SnFormField } from './SnFormField';
import { StoreFormField } from './StoreFormField';
import { StyleFilter } from './style';
import FormItem from 'antd/lib/form/FormItem';
import { ApplyDateFormField } from './ApplyDateFormField';
import { useMemo, useState } from 'react';
import { tradeReturnListFilterFoldCache } from 'services/cache';
import dayjs, { Dayjs } from 'lib/dayjs';
import DueTimeEnd from './DueTimeEnd';

interface MercadoFilterProps {
  onChange: (e: FilterChangeEvent) => void;
}

const defaultFormValues = {
  ...initValues,
  dateRange: [dayjs(dayjs().subtract(30, 'days').format('YYYY-MM-DD')), dayjs()],
};

export const MercadoFilter = (props: MercadoFilterProps) => {
  const { onChange } = props;
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const [fold, setFold] = useState(tradeReturnListFilterFoldCache.get() ?? false);

  const [disabled, setDisabled] = useState(false);

  const handleClearSubmit = useMemoizedFn(() => {
    form.resetFields();
    form.submit();
    setDisabled(false);
  });
  const handleSearchSubmit = useMemoizedFn(() => {
    form.submit();
  });
  const handleFormFinish = useMemoizedFn((e: any) => {
    const { sn, hasNote } = e;
    onChange({
      ...e,
      hasNote,
      sn: !sn ? undefined : sn,
    });
  });
  const handleValuesChange = useMemoizedFn((_, values: any) => {
    if ((values.snType === 'orderId' || values.snType === 'returnId') && values.sn) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  });
  const handleFold = useMemoizedFn(() => {
    setFold((prev) => {
      tradeReturnListFilterFoldCache.set(!prev);
      return !prev;
    });
  });

  const {
    returnReasonSelectOptions,
    operateSelectOptions,
    returnTypeSelectOptions,
    displayStatusSelectOptions,
  } = useMemo(() => {
    return {
      returnReasonSelectOptions: [
        {
          value: 'BUYER_REMORSE_OR_NOT_NEEDED',
          label: t('买家反悔／不再需要'),
        },
        {
          value: 'NOT_WORKING_OR_DEFECTIVE',
          label: t('商品无法使用／存在故障'),
        },
        {
          value: 'DAMAGED_OR_BROKEN',
          label: t('商品损坏／破损'),
        },
        {
          value: 'DELIVERED_NOT_RECEIVED',
          label: t('显示送达但未收到'),
        },
        {
          value: 'MISSING_ITEM_OR_PARTS',
          label: t('商品或配件缺失'),
        },
        {
          value: 'NOT_AS_DESCRIBED',
          label: t('与商品描述不符'),
        },
        {
          value: 'SIZE_OR_COLOR_MISMATCH',
          label: t('尺寸或颜色不符'),
        },
        {
          value: 'WRONG_ITEM',
          label: t('收到错误商品'),
        },
        {
          value: 'DELIVERY_OR_FULFILLMENT_ISSUE',
          label: t('配送／履约异常'),
        },
        {
          value: 'EMPTY_PACKAGE',
          label: t('空包裹'),
        },
        {
          value: 'MISSING_INVOICE',
          label: t('缺少发票'),
        },
        {
          value: 'VEHICLE_INCOMPATIBLE',
          label: t('与车辆不兼容'),
        },
        {
          value: 'CHANGE_DELIVERY_ADDRESS',
          label: t('aide.修改收货地址'),
        },
        {
          value: 'OUT_OF_STOCK',
          label: t('商品缺货'),
        },
        {
          value: 'UNAUTHORIZED_PURCHASE',
          label: t('未授权购买'),
        },
        {
          value: 'OTHER',
          label: t('OTHER'),
        },
      ],
      operateSelectOptions: [
        {
          value: 'RETURN_REVIEW_FAIL',
          label: t('退货验收不通过'),
        },
        {
          value: 'CONFIRM_ONLY_REFUND',
          label: t('btn_agree_only_refund'),
        },
        {
          value: 'DISPUTE',
          label: t('发起平台调解'),
        },
        {
          value: 'RETURN_REVIEW_OK',
          label: t('退货验收通过'),
        },
        {
          value: 'OFFER',
          label: t('trade.提出部分退款'),
        },
        {
          value: 'CONFIRM_RETURN_REFUND',
          label: t('btn_agree_refunds'),
        },
      ],
      returnTypeSelectOptions: [
        {
          value: 'MEDIATION',
          label: t('调解中'),
        },
        {
          value: 'CHANGE',
          label: t('换货'),
        },
        {
          value: 'RETURN_REFUND',
          label: t('WhatsApp.msgTemplateType.SALES_RETURN'),
        },
      ],
      displayStatusSelectOptions: [
        {
          value: 'CANCELLED',
          label: t('申请已取消'),
        },
        {
          value: 'PLATFORM_CLOSED',
          label: t('平台已关闭'),
        },
        {
          value: 'REFUND_COMPLETED',
          label: t('退款已完成'),
        },
        {
          value: 'IN_DISPUTE',
          label: t('mercado.returnStatus.IN_DISPUTE'),
        },
        {
          value: 'WAITING_BUYER_RETURN',
          label: t('mercado.returnStatus.WAITING_BUYER_RETURN'),
        },
        {
          value: 'PARTIALLY_REFUNDED',
          label: t('mercado.returnStatus.PARTIALLY_REFUNDED'),
        },
        {
          value: 'REVERSE_PENDING',
          label: t('mercado.returnStatus.REVERSE_PENDING'),
        },
        {
          value: 'WAITING_SELLER_ACCEPT_RETURN',
          label: t('等待卖家验收退货'),
        },
        {
          value: 'UNKNOWN',
          label: t('leChatLogisticsStatus.LE_CHAT_UNKNOWN_TRACE'),
        },
        {
          value: 'SENT_PARTIAL_REFUND',
          label: t('mercado.returnStatus.SENT_PARTIAL_REFUND'),
        },
      ],
    };
  }, [t]);

  const disableDate = useMemoizedFn((current: Dayjs) => {
    const bigThanToday = current.isAfter(dayjs(), 'days');
    const lessThan90 = current.isBefore(dayjs().subtract(90, 'days'), 'days');
    return bigThanToday || lessThan90;
  });

  return (
    <div className="bg-fill-0 flex flex-col p-4">
      <StyleFilter
        form={form}
        initialValues={defaultFormValues}
        onFinish={handleFormFinish}
        onValuesChange={handleValuesChange}
      >
        <div className="filter-list">
          <SnFormField
            platform="MERCADO"
            filterType="return"
            validate={false}
            onValuesChange={(values) => handleValuesChange({}, values)}
          />
          <StoreFormField disabled={disabled} platform="MERCADO" filterType="return" />
          <FormItem name="reason">
            <Select
              dropdownMatchSelectWidth={false}
              disabled={disabled}
              mode="multiple"
              maxTagCount="responsive"
              placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnReason')}
              className="min-w-[160px]"
              allowClear
              showArrow
              options={returnReasonSelectOptions}
            />
          </FormItem>
          <ApplyDateFormField
            disabled={disabled}
            filterType="return"
            form={form}
            disabledDate={disableDate}
          />
        </div>
        {!fold ? (
          <>
            <div className="filter-list">
              <FormItem name="operate">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.availableAction')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={operateSelectOptions}
                />
              </FormItem>
              <FormItem name="returnType">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnType')}
                  className="!w-[10vw]"
                  allowClear
                  showArrow
                  options={returnTypeSelectOptions}
                />
              </FormItem>
              <FormItem name="displayStatus">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnStatus')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={displayStatusSelectOptions}
                />
              </FormItem>
              <FormItem name="mercadoDueTimeEnd" label={t('处理截止时间')}>
                <DueTimeEnd disabled={disabled} />
              </FormItem>
              <FormItem
                label={t('scene.orderWorkspace.returnRefundTab.filter.reputationOnly')}
                name="reputation"
                valuePropName="checked"
              >
                <Switch />
              </FormItem>
            </div>
          </>
        ) : null}
        <div className="flex gap-4">
          <FormItem noStyle>
            <Button
              type="primary"
              htmlType="submit"
              className="!min-w-32"
              onClick={handleSearchSubmit}
            >
              {t('scene.orderWorkspace.returnRefundTab.filter.searchOrder')}
            </Button>
          </FormItem>
          <Button type="link" className="!px-1" onClick={handleClearSubmit}>
            <ClearOutlined />
            {t('scene.orderWorkspace.returnRefundTab.filter.clearFilters')}
          </Button>
          <Button type="link" className="!px-1" onClick={handleFold}>
            {fold ? (
              <>
                {t('scene.orderWorkspace.returnRefundTab.filter.expandFilters')}
                <DownOutlined className="ml-2 text-xs" />
              </>
            ) : (
              <>
                {t('scene.orderWorkspace.returnRefundTab.filter.collapseFilters')}
                <UpOutlined className="ml-2 text-xs" />
              </>
            )}
          </Button>
        </div>
      </StyleFilter>
    </div>
  );
};
